import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Mail, Phone, MapPin, Calendar, DollarSign, MessageSquare, Download, StickyNote } from 'lucide-react'
import { AdminLayout, Breadcrumb } from '@/components/layout/AdminLayout'
import { StatusBadge } from '@/components/admin/StatusBadge'
import { Spinner } from '@/components/ui/Spinner'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { fetchLeadById, updateLeadStatus, updateLeadAdminNotes, getLeadPDFUrl } from '@/lib/api'
import { formatCurrency } from '@/lib/pricing'
import { LEAD_STATUSES } from '@/types'
import type { Lead, LeadOption } from '@/types'

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>()
  const [lead, setLead] = useState<Lead | null>(null)
  const [options, setOptions] = useState<LeadOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusValue, setStatusValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [adminNotes, setAdminNotes] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)
  const [notesSaved, setNotesSaved] = useState(false)

  useEffect(() => {
    if (!id) return
    fetchLeadById(Number(id))
      .then(({ lead: l, options: opts }) => {
        setLead(l)
        setOptions(opts)
        setStatusValue(l.status)
        setAdminNotes(l.admin_notes ?? '')
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [id])

  const handleNotesSave = async () => {
    if (!lead) return
    setNotesSaving(true)
    try {
      await updateLeadAdminNotes(lead.id, adminNotes)
      setLead({ ...lead, admin_notes: adminNotes || null })
      setNotesSaved(true)
      setTimeout(() => setNotesSaved(false), 2000)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setNotesSaving(false)
    }
  }

  const handleStatusSave = async () => {
    if (!lead || statusValue === lead.status) return
    setSaving(true)
    try {
      await updateLeadStatus(lead.id, statusValue)
      setLead({ ...lead, status: statusValue })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <AdminLayout><div className="flex justify-center py-16"><Spinner size="lg" /></div></AdminLayout>
  if (error || !lead) return <AdminLayout><div className="text-red-600 py-4">{error || 'Lead not found'}</div></AdminLayout>

  const upgradeTotal = options.reduce((s, o) => s + o.option_price_snapshot, 0)
  const groupedOptions = options.reduce<Record<string, LeadOption[]>>((acc, opt) => {
    const key = opt.category_name_snapshot
    if (!acc[key]) acc[key] = []
    acc[key].push(opt)
    return acc
  }, {})

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-0">
        <Breadcrumb items={[{ label: 'Leads', to: '/admin/leads' }, { label: lead.customer_name }]} />
        <a
          href={getLeadPDFUrl(lead.id)}
          download
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors mb-6"
        >
          <Download className="h-4 w-4" /> Download PDF
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Customer + Status */}
        <div className="lg:col-span-1 space-y-4">
          {/* Customer card */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-card">
            <h2 className="font-semibold text-slate-800 mb-4">Customer</h2>
            <div className="space-y-3">
              <div className="font-bold text-slate-900 text-lg">{lead.customer_name}</div>
              <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-600 transition-colors">
                <Mail className="h-4 w-4 text-slate-400" />{lead.email}
              </a>
              <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-600 transition-colors">
                <Phone className="h-4 w-4 text-slate-400" />{lead.phone}
              </a>
              {lead.location && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="h-4 w-4 text-slate-400" />{lead.location}
                </div>
              )}
              {lead.timeline && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="h-4 w-4 text-slate-400" />{lead.timeline}
                </div>
              )}
              {lead.budget_range && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="h-4 w-4 text-slate-400" />{lead.budget_range}
                </div>
              )}
              {lead.notes && (
                <div className="flex items-start gap-2 text-sm text-slate-600">
                  <MessageSquare className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{lead.notes}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-card">
            <h2 className="font-semibold text-slate-800 mb-3">Status</h2>
            <div className="mb-3"><StatusBadge status={lead.status} /></div>
            <Select
              options={LEAD_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
            />
            {statusValue !== lead.status && (
              <Button fullWidth className="mt-3" loading={saving} onClick={handleStatusSave}>
                Save Status
              </Button>
            )}
          </div>

          {/* Admin notes */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-card">
            <h2 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-slate-400" /> Internal Notes
            </h2>
            <textarea
              rows={4}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes visible only to your team…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-y text-slate-700 placeholder:text-slate-300"
            />
            <button
              onClick={handleNotesSave}
              disabled={notesSaving}
              className="mt-2 w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {notesSaving ? 'Saving…' : notesSaved ? 'Saved ✓' : 'Save Notes'}
            </button>
          </div>

          {/* Meta */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-card">
            <h2 className="font-semibold text-slate-800 mb-3">Submission Info</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Submitted</span>
                <span>{new Date(lead.created_at).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>Disclaimer v</span>
                <span>{lead.disclaimer_version}</span>
              </div>
              {lead.pricing_valid_until_snapshot && (
                <div className="flex justify-between text-slate-500">
                  <span>Quote valid</span>
                  <span>{lead.pricing_valid_until_snapshot}</span>
                </div>
              )}
              {lead.financing_interest === 1 && (
                <div className="text-brand-600 text-xs font-medium">Interested in financing</div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Build summary */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
            <div className="bg-dark-900 px-6 py-4">
              <div className="flex items-baseline justify-between">
                <h2 className="text-white font-bold text-lg">{lead.selected_model_name_snapshot}</h2>
                <span className="text-brand-400 font-bold text-xl">{formatCurrency(lead.estimated_total_snapshot)}</span>
              </div>
              <div className="text-slate-400 text-sm mt-0.5">
                Base: {formatCurrency(lead.base_price_snapshot)}
              </div>
            </div>

            <div className="p-6">
              {options.length === 0 ? (
                <p className="text-slate-400 text-sm italic">No upgrades selected.</p>
              ) : (
                <div className="space-y-5 mb-6">
                  {Object.entries(groupedOptions).map(([cat, opts]) => (
                    <div key={cat}>
                      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{cat}</h3>
                      <div className="space-y-2">
                        {opts.map((opt, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-slate-700">{opt.option_name_snapshot}</span>
                            <span className="text-slate-600 font-medium">{formatCurrency(opt.option_price_snapshot)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t border-slate-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Base Model</span><span>{formatCurrency(lead.base_price_snapshot)}</span>
                </div>
                {upgradeTotal > 0 && (
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>Upgrades ({options.length})</span><span>+{formatCurrency(upgradeTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-100">
                  <span>Estimated Total</span>
                  <span className="text-brand-600 text-lg">{formatCurrency(lead.estimated_total_snapshot)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer snapshot */}
          {lead.disclaimer_snapshot && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Disclaimer shown at submission</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{lead.disclaimer_snapshot}</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
